import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, Loader2, AlertCircle, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui';
import { useCreateCompany } from '@/services/mutations/company';
import { useSetCompanyPassword } from '@/services/mutations/password';
import {
	PASSWORD_BACKEND_INVALID_MESSAGE,
	PASSWORD_BACKEND_MAX_LENGTH,
	PASSWORD_BACKEND_MIN_LENGTH,
	validatePasswordBackend,
} from '@/utils/validations';

interface CompanyModalProps {
	isOpen: boolean;
	onClose: () => void;
}

type ModalStep = 'company' | 'password';

const getCompanyDisplayName = (company: Companies.Company) =>
	company.trade_name?.trim() || company.legal_name?.trim() || 'Empresa';

export const CompanyModal = ({ isOpen, onClose }: CompanyModalProps) => {
	const [step, setStep] = useState<ModalStep>('company');
	const [createdCompany, setCreatedCompany] =
		useState<Companies.Company | null>(null);
	const [formData, setFormData] = useState({
		legal_name: '',
		trade_name: '',
		cnpj: '',
		billing_email: '',
		banner_img_url: '',
		logo_img_url: '',
	});
	const [passwordData, setPasswordData] = useState({
		password: '',
		confirm_password: '',
	});

	const [errors, setErrors] = useState<Record<string, string>>({});

	const resetAndClose = () => {
		setStep('company');
		setCreatedCompany(null);
		setPasswordData({ password: '', confirm_password: '' });
		setErrors({});
		setFormData({
			legal_name: '',
			trade_name: '',
			cnpj: '',
			billing_email: '',
			banner_img_url: '',
			logo_img_url: '',
		});
		onClose();
	};

	const { mutateAsync: createCompany, isPending: isCreating } =
		useCreateCompany((company) => {
			setCreatedCompany(company);
			setStep('password');
			setErrors({});
		});

	const { mutateAsync: setCompanyPassword, isPending: isSavingPassword } =
		useSetCompanyPassword(resetAndClose);

	const isPending = isCreating || isSavingPassword;
	const isPasswordStep = step === 'password';

	useEffect(() => {
		if (isOpen) {
			setStep('company');
			setCreatedCompany(null);
			setPasswordData({ password: '', confirm_password: '' });
			setErrors({});
			setFormData({
				legal_name: '',
				trade_name: '',
				cnpj: '',
				billing_email: '',
				banner_img_url: '',
				logo_img_url: '',
			});
		}
	}, [isOpen]);

	const validateCompany = () => {
		const newErrors: Record<string, string> = {};
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

		if (!formData.trade_name.trim()) newErrors.trade_name = 'Obrigatório';

		const cleanCNPJ = formData.cnpj.replace(/\D/g, '');
		if (!formData.cnpj.trim()) newErrors.cnpj = 'Obrigatório';
		else if (cleanCNPJ.length !== 14)
			newErrors.cnpj = 'CNPJ deve ter 14 dígitos';

		if (!formData.billing_email.trim())
			newErrors.billing_email = 'Obrigatório';
		else if (!emailRegex.test(formData.billing_email))
			newErrors.billing_email = 'E-mail inválido';

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const validatePasswordStep = () => {
		const newErrors: Record<string, string> = {};

		if (!passwordData.password.trim()) {
			newErrors.password = 'Obrigatório';
		} else if (passwordData.password.length < PASSWORD_BACKEND_MIN_LENGTH) {
			newErrors.password = 'A senha deve ter no mínimo 6 caracteres';
		} else if (passwordData.password.length > PASSWORD_BACKEND_MAX_LENGTH) {
			newErrors.password = 'A senha deve ter no máximo 255 caracteres';
		} else if (!validatePasswordBackend(passwordData.password)) {
			newErrors.password = PASSWORD_BACKEND_INVALID_MESSAGE;
		}

		if (!passwordData.confirm_password.trim()) {
			newErrors.confirm_password = 'Obrigatório';
		} else if (passwordData.password !== passwordData.confirm_password) {
			newErrors.confirm_password = 'As senhas não coincidem';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleCreateCompany = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!validateCompany()) return;

		const payload: Companies.Create = {
			trade_name: formData.trade_name.trim(),
			cnpj: formData.cnpj.replace(/\D/g, ''),
			billing_email: formData.billing_email.trim(),
			...(formData.legal_name.trim() && {
				legal_name: formData.legal_name.trim(),
			}),
			...(formData.banner_img_url.trim() && {
				banner_img_url: formData.banner_img_url.trim(),
			}),
			...(formData.logo_img_url.trim() && {
				logo_img_url: formData.logo_img_url.trim(),
			}),
		};

		await createCompany(payload);
	};

	const handleSavePassword = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!createdCompany) return;
		if (!validatePasswordStep()) return;

		await setCompanyPassword({
			companyId: createdCompany.id,
			newPassword: passwordData.password,
		});
	};

	const formatCNPJInput = (value: string) => {
		return value
			.replace(/\D/g, '')
			.replace(/^(\d{2})(\d)/, '$1.$2')
			.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
			.replace(/\.(\d{3})(\d)/, '.$1/$2')
			.replace(/(\d{4})(\d)/, '$1-$2')
			.slice(0, 18);
	};

	if (!isOpen) return null;

	const labelClass =
		'ml-1 text-[10px] font-black uppercase tracking-widest text-stone-300';
	const inputBaseClass =
		'w-full bg-stone-50 border rounded-xl px-4 py-3 text-sm font-bold text-stone-600 outline-none transition-all box-border disabled:opacity-50';

	const getFieldClass = (fieldName: string) =>
		cn(
			inputBaseClass,
			errors[fieldName]
				? 'border-rose-500 bg-rose-50/30 focus:border-rose-600'
				: 'border-stone-200 focus:border-stone-400 focus:bg-white',
		);

	const ErrorMsg = ({ field }: { field: string }) =>
		errors[field] ? (
			<span className="animate-in fade-in slide-in-from-top-1 ml-1 mt-1 flex items-center gap-1 text-[10px] font-bold text-rose-500">
				<AlertCircle size={10} /> {errors[field]}
			</span>
		) : null;

	const modalHeader = (
		<div className="flex shrink-0 items-center justify-between border-b border-stone-50 px-8 py-6">
			<div>
				<h2 className="text-lg font-black text-stone-900">
					{isPasswordStep ? 'Senha de acesso' : 'Nova Empresa'}
				</h2>
				{isPasswordStep && createdCompany ? (
					<p className="mt-1 text-xs font-medium text-stone-400">
						{getCompanyDisplayName(createdCompany)} — login no
						portal com CNPJ e esta senha
					</p>
				) : null}
			</div>
			<button
				type="button"
				onClick={resetAndClose}
				disabled={isPending}
				className="text-stone-400 transition-colors hover:text-stone-600 disabled:opacity-50"
			>
				<X size={20} />
			</button>
		</div>
	);

	const passwordFields = (
		<>
			<div>
				<label className={labelClass}>SENHA DE ACESSO</label>
				<input
					type="password"
					autoComplete="new-password"
					value={passwordData.password}
					onChange={(e) =>
						setPasswordData({
							...passwordData,
							password: e.target.value,
						})
					}
					className={getFieldClass('password')}
					placeholder="Mín. 6 caracteres, com letra e número"
				/>
				<ErrorMsg field="password" />
			</div>
			<div>
				<label className={labelClass}>CONFIRMAR SENHA</label>
				<input
					type="password"
					autoComplete="new-password"
					value={passwordData.confirm_password}
					onChange={(e) =>
						setPasswordData({
							...passwordData,
							confirm_password: e.target.value,
						})
					}
					className={getFieldClass('confirm_password')}
					placeholder="Repita a senha"
				/>
				<ErrorMsg field="confirm_password" />
			</div>
		</>
	);

	const companyFields = (
		<>
			<div>
				<label className={labelClass}>NOME FANTASIA</label>
				<input
					type="text"
					value={formData.trade_name}
					onChange={(e) =>
						setFormData({ ...formData, trade_name: e.target.value })
					}
					className={getFieldClass('trade_name')}
					placeholder="Ex: Exemplo Corp"
				/>
				<ErrorMsg field="trade_name" />
			</div>
			<div>
				<label className={labelClass}>RAZÃO SOCIAL (opcional)</label>
				<input
					type="text"
					value={formData.legal_name}
					onChange={(e) =>
						setFormData({ ...formData, legal_name: e.target.value })
					}
					className={getFieldClass('legal_name')}
					placeholder="Ex: Empresa Exemplo Ltda."
				/>
				<ErrorMsg field="legal_name" />
			</div>
			<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
				<div>
					<label className={labelClass}>CNPJ</label>
					<input
						type="text"
						value={formData.cnpj}
						onChange={(e) =>
							setFormData({
								...formData,
								cnpj: formatCNPJInput(e.target.value),
							})
						}
						className={getFieldClass('cnpj')}
						placeholder="00.000.000/0000-00"
					/>
					<ErrorMsg field="cnpj" />
				</div>
				<div>
					<label className={labelClass}>E-MAIL DE FATURAMENTO</label>
					<input
						type="email"
						value={formData.billing_email}
						onChange={(e) =>
							setFormData({
								...formData,
								billing_email: e.target.value,
							})
						}
						className={getFieldClass('billing_email')}
						placeholder="financeiro@empresa.com"
					/>
					<ErrorMsg field="billing_email" />
				</div>
			</div>
			<div>
				<label className={labelClass}>
					URL DA IMAGEM DO LOGO (opcional)
				</label>
				<input
					type="text"
					value={formData.logo_img_url}
					onChange={(e) =>
						setFormData({
							...formData,
							logo_img_url: e.target.value,
						})
					}
					className={getFieldClass('logo_img_url')}
					placeholder="https://url_exemplo_teste.com"
				/>
				<ErrorMsg field="logo_img_url" />
			</div>
			<div>
				<label className={labelClass}>
					URL DA IMAGEM DO BANNER (opcional)
				</label>
				<input
					type="text"
					value={formData.banner_img_url}
					onChange={(e) =>
						setFormData({
							...formData,
							banner_img_url: e.target.value,
						})
					}
					className={getFieldClass('banner_img_url')}
					placeholder="https://url_exemplo_teste.com"
				/>
				<ErrorMsg field="banner_img_url" />
			</div>
		</>
	);

	const modalFooter = (
		<div className="flex shrink-0 items-center justify-end gap-6 border-t border-stone-50 p-8">
			<button
				type="button"
				onClick={resetAndClose}
				disabled={isPending}
				className="text-sm font-bold text-stone-400 hover:text-stone-600 disabled:opacity-50"
			>
				{isPasswordStep ? 'Pular' : 'Cancelar'}
			</button>
			<Button
				type="submit"
				text={
					isPasswordStep
						? isSavingPassword
							? 'Salvando...'
							: 'Salvar senha'
						: isCreating
						  ? 'Criando...'
						  : 'Criar Empresa'
				}
				disabled={isPending}
				className="min-w-[160px] rounded-full bg-black px-8 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-neutral-800"
				startIcon={
					isPending ? (
						<Loader2 className="size-4 animate-spin" />
					) : isPasswordStep ? (
						<Lock className="size-4 stroke-[3]" />
					) : (
						<Check className="size-4 stroke-[3]" />
					)
				}
			/>
		</div>
	);

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={isPending ? undefined : resetAndClose}
			/>
			<div className="animate-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-[520px] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200">
				<form
					onSubmit={
						isPasswordStep
							? handleSavePassword
							: handleCreateCompany
					}
					noValidate
					className="flex flex-col overflow-hidden"
				>
					{modalHeader}
					<div className="flex-1 space-y-6 overflow-y-auto p-8">
						{isPasswordStep ? passwordFields : companyFields}
					</div>
					{modalFooter}
				</form>
			</div>
		</div>,
		document.body,
	);
};
